import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, Pencil, PlusCircle, Save, Trash2 } from 'lucide-react';
import { lettersApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { SignaturePreview } from './SignaturePreview';
import { SignatureLayoutEditor } from './SignatureLayoutEditor';
import { buildLetterRouteUrl } from '../../utils/letterRoutes';
import { isAdminFlag } from '../../utils/helpers';

interface SignatoryItem {
  id: number;
  name: string;
  email: string;
  signer_name?: string;
  designation?: string;
  signature_html?: string;
  has_signature?: boolean | number;
  signature_updated_at?: string;
}

interface SignatorySignature {
  id: number;
  name: string;
  email: string;
  signer_name: string;
  designation: string;
  details_text: string;
  signature_html: string;
  updated_at?: string;
}

interface LettersAddSignaturePageProps {
  onNavigate?: (page: string) => void;
}

const DEFAULT_SIGNATURE_COMPANY_NAME = 'Surbhi Electronet Pvt. Ltd.';

export function LettersAddSignaturePage({ onNavigate }: LettersAddSignaturePageProps = {}) {
  const [signatories, setSignatories] = useState<SignatoryItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'editor'>('list');
  const [cameFromAdmin, setCameFromAdmin] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const { user } = useAuth();
  // is_admin arrives from the SSO cookie as a MySQL tinyint (1/0), so it must go
  // through isAdminFlag; `=== true` made real admins fall into the "own signature
  // only" branch below and hid every other user from the signature list.
  const isAdmin = isAdminFlag(user?.permissions?.is_admin);
  const currentUserId = user?.id ? parseInt(user.id, 10) : null;
  const [loadingSignature, setLoadingSignature] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [signerName, setSignerName] = useState('');
  const [designation, setDesignation] = useState('');
  const [detailsText, setDetailsText] = useState('');
  const [companyName, setCompanyName] = useState(DEFAULT_SIGNATURE_COMPANY_NAME);
  const [signatureHtml, setSignatureHtml] = useState('<p></p>');
  const [redirectHandled, setRedirectHandled] = useState(false);

  const selectedUser = useMemo(
    () => signatories.find((s) => s.id === selectedId) || null,
    [signatories, selectedId],
  );

  const loadSignatories = async () => {
    try {
      setLoadingList(true);
      setError('');
      const response = await lettersApi.listSignatureManagementUsers();
      const rows = response.data || [];
      // If not admin, filter to show only current user's signature
      const filtered =
        !isAdmin && currentUserId
          ? rows.filter((r: SignatoryItem) => Number(r.id) === currentUserId)
          : rows;
      setSignatories(filtered);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load signatories.');
      setSignatories([]);
      setSelectedId(null);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadSignatories();
  }, []);

  // Check for localStorage redirect to open designer for specific user
  useEffect(() => {
    if (loadingList || redirectHandled) return;

    const shouldOpenDesigner = localStorage.getItem('letters_signature_open_designer') === '1';
    const fromAdmin = localStorage.getItem('letters_signature_from_admin') === '1';
    const targetRaw = localStorage.getItem('letters_signature_target_user_id');

    // Clear localStorage first
    localStorage.removeItem('letters_signature_open_designer');
    localStorage.removeItem('letters_signature_from_admin');
    localStorage.removeItem('letters_signature_target_user_id');

    if (fromAdmin) setCameFromAdmin(true);

    // Always open editor directly — either for the target user or the first user
    if (!shouldOpenDesigner || !targetRaw) {
      // No specific target — open editor for first user directly
      if (signatories.length > 0) {
        const firstId = signatories[0].id;
        setSelectedId(firstId);
        setViewMode('editor');
        void loadSignature(firstId);
      }
      setRedirectHandled(true);
      return;
    }

    const targetId = Number(targetRaw);
    if (!Number.isFinite(targetId)) {
      // Invalid target ID — open editor for first user
      if (signatories.length > 0) {
        const firstId = signatories[0].id;
        setSelectedId(firstId);
        setViewMode('editor');
        void loadSignature(firstId);
      }
      setRedirectHandled(true);
      return;
    }

    const existsInList = signatories.some((item) => item.id === targetId);
    if (existsInList) {
      setSelectedId(targetId);
      setViewMode('editor');
      void loadSignature(targetId);
    } else {
      // Target user not found — open editor for first user
      if (signatories.length > 0) {
        const firstId = signatories[0].id;
        setSelectedId(firstId);
        setViewMode('editor');
        void loadSignature(firstId);
      }
    }

    setRedirectHandled(true);
  }, [loadingList, redirectHandled, signatories]);

  const loadSignature = async (userId: number) => {
    try {
      setLoadingSignature(true);
      setError('');
      const response = await lettersApi.getSignatorySignature(userId);
      const data: SignatorySignature = response.data;
      setSignerName(data.signer_name || data.name || '');
      setDesignation(data.designation || '');
      setDetailsText(data.details_text || '');
      setSignatureHtml(data.signature_html || '<p></p>');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load signature details.');
      setSignerName('');
      setDesignation('');
      setDetailsText('');
      setCompanyName(DEFAULT_SIGNATURE_COMPANY_NAME);
      setSignatureHtml('<p></p>');
    } finally {
      setLoadingSignature(false);
    }
  };

  useEffect(() => {
    if (!selectedId) return;
    loadSignature(selectedId);
  }, [selectedId]);

  const saveSignature = async () => {
    if (!selectedId) return;
    try {
      setSaving(true);
      setError('');
      await lettersApi.saveSignatorySignature(selectedId, {
        signer_name: signerName,
        designation,
        details_text: detailsText,
        signature_html: signatureHtml,
      });
      setSuccess('Signature saved successfully.');
      window.setTimeout(() => setSuccess(''), 2000);
      await loadSignatories();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save signature.');
    } finally {
      setSaving(false);
    }
  };

  const openDesigner = async (userId: number) => {
    setSelectedId(userId);
    setViewMode('editor');
    await loadSignature(userId);
  };

  const deleteSignature = async () => {
    if (!selectedId) return;
    const confirmed = window.confirm('Delete this saved signature for the selected user?');
    if (!confirmed) return;

    try {
      setDeleting(true);
      setError('');
      await lettersApi.deleteSignatorySignature(selectedId);
      setSignatureHtml('<p></p>');
      setSuccess('Signature deleted successfully.');
      window.setTimeout(() => setSuccess(''), 2000);
      await loadSignatories();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete signature.');
    } finally {
      setDeleting(false);
    }
  };

  const goToList = () => {
    if (onNavigate) {
      onNavigate('letters-admin');
      return;
    }

    const origin =
      typeof window !== 'undefined' ? window.location.origin : 'https://letter.surbhi.net';
    window.location.href = buildLetterRouteUrl(origin, 'letters-admin');
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Add Signature</h1>
        <p className="text-gray-600 mt-2">
          Manage signature profiles and open a dedicated designer for each authorized signatory.
        </p>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          ⚠ {error}
        </div>
      )}
      {success && (
        <div className="px-4 py-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">
          ✓ {success}
        </div>
      )}

      {viewMode === 'list' ? (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 md:p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-semibold text-gray-900">Authorized Signatories</h2>
            <button
              onClick={loadSignatories}
              className="text-sm text-violet-700 hover:text-violet-800"
            >
              Refresh
            </button>
          </div>

          {loadingList ? (
            <div className="text-sm text-gray-500 py-8">Loading signatories...</div>
          ) : signatories.length === 0 ? (
            <div className="text-sm text-gray-500 py-8">
              No authorized signatories found. Assign roles first.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {signatories.map((item) => {
                const hasSignature = item.has_signature === true || item.has_signature === 1;
                return (
                  <div key={item.id} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-base font-semibold text-gray-900">{item.name}</div>
                        <div className="text-sm text-gray-600">{item.email}</div>
                        <div
                          className={`mt-1 text-xs font-medium ${hasSignature ? 'text-emerald-700' : 'text-amber-700'}`}
                        >
                          {hasSignature ? 'Signature available' : 'Signature not created'}
                        </div>
                      </div>
                    </div>

                    {hasSignature && item.signature_html ? (
                      <div className="mt-3 rounded-lg border border-gray-200 bg-white p-2 overflow-hidden">
                        <div
                          className="origin-top-left scale-[0.55] w-[180%] -mb-16"
                          dangerouslySetInnerHTML={{ __html: item.signature_html }}
                        />
                      </div>
                    ) : (
                      <div className="mt-3 rounded-lg border border-dashed border-gray-300 bg-white p-4 text-sm text-gray-500">
                        No saved signature preview.
                      </div>
                    )}

                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={() => openDesigner(item.id)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700"
                      >
                        {hasSignature ? (
                          <Pencil className="w-4 h-4" />
                        ) : (
                          <PlusCircle className="w-4 h-4" />
                        )}
                        {hasSignature ? 'Edit Signature' : 'Create Signature'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-5">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 md:p-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <button
                onClick={goToList}
                className="inline-flex items-center gap-2 text-sm font-medium text-violet-700 hover:text-violet-800"
              >
                <ArrowLeft className="w-4 h-4" /> Back to Signature Management
              </button>
              <h2 className="text-xl font-semibold text-gray-900 mt-1">Signature Designer</h2>
              <p className="text-sm text-gray-600">
                {selectedUser?.name} ({selectedUser?.email})
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={deleteSignature}
                disabled={saving || deleting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-60"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {deleting ? 'Deleting...' : 'Delete Signature'}
              </button>
              <button
                onClick={saveSignature}
                disabled={saving || deleting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? 'Saving...' : 'Save Signature'}
              </button>
            </div>
          </div>

          {loadingSignature ? (
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-10 text-center text-gray-500 inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading signature...
            </div>
          ) : (
            <>
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Profile Details</h3>
                  <span className="text-xs font-medium text-slate-700 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1">
                    Basic Info
                  </span>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4 md:p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="w-full h-10 px-3 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                        placeholder="Enter company name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Signatory Name
                      </label>
                      <input
                        type="text"
                        value={signerName}
                        onChange={(e) => setSignerName(e.target.value)}
                        className="w-full h-10 px-3 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                        placeholder="Enter signatory name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Designation
                      </label>
                      <input
                        type="text"
                        value={designation}
                        onChange={(e) => setDesignation(e.target.value)}
                        className="w-full h-10 px-3 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                        placeholder="Enter designation"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Address / Additional Details
                      </label>
                      <input
                        type="text"
                        value={detailsText}
                        onChange={(e) => setDetailsText(e.target.value)}
                        className="w-full h-10 px-3 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                        placeholder="Department, office address, contact details"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Edit Signature</h3>
                    <span className="text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2.5 py-1">
                      Designer
                    </span>
                  </div>
                  <SignatureLayoutEditor
                    valueHtml={signatureHtml}
                    signerName={signerName}
                    designation={designation}
                    detailsText={detailsText}
                    companyName={companyName}
                    onCompanyNameChange={setCompanyName}
                    onChangeHtml={setSignatureHtml}
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Preview</h3>
                    <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                      Live
                    </span>
                  </div>
                  <SignaturePreview html={signatureHtml} />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
